import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/trade/i18n/useTranslations';
import { convertWeightToDisplayValue } from '@rematter/modules/trade/utils/weight';
import {
  MeasuredValue,
  cn,
  humanReadableCurrency,
  humanReadableShortPricingUnit,
  humanReadableNumber,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import {
  useReconciliationSummary,
  FormulaValue,
} from './hooks/useReconciliationSummary';

import {
  $materialsReconciliationForm,
  MaterialsReconciliationFormState,
} from '~/modules/trade/_state/loadReconciliation.state';
import { WeightUnitType } from '~/modules/trade/types/load';
import { getLocale } from '~/shared/utils/locale';

type FormulaValueWithMonetary = FormulaValue & {
  monetaryValue?: number;
};

type FormulaDisplayProps = {
  netShipped: FormulaValueWithMonetary;
  netReceived: FormulaValueWithMonetary;
  deduction: FormulaValueWithMonetary;
  delta: FormulaValueWithMonetary;
  formatValue?: (value: number) => string;
  valuePrefix?: string;
  valueSuffix?: string;
  showColors?: boolean;
  type: 'weight' | 'monetary';
  titles?: {
    netShipped?: string;
    netReceived?: string;
    deduction?: string;
    delta?: string;
  };
};

const deductionClassName = 'text-failure'; // Always red for deductions

const FormulaDisplay: React.FC<FormulaDisplayProps> = ({
  netShipped,
  netReceived,
  deduction,
  delta,
  formatValue,
  valuePrefix = '',
  valueSuffix = '',
  showColors = true,
  type = 'weight',
  titles = {},
}) => {
  const getDeltaColor = (value: FormulaValueWithMonetary) => {
    if (!showColors) return 'text-neutral bg-neutral/5';

    const numericValue = !value.monetaryValue
      ? (value.weight?.value ?? value.each ?? 0)
      : value.monetaryValue;

    if (type === 'weight') {
      // For weight: negative is bad (failure)
      return numericValue < 0
        ? 'text-failure bg-failure/10'
        : 'text-neutral bg-neutral/5';
    } else {
      // For monetary: positive is good (success), negative is bad (failure)
      if (numericValue > 0) return 'text-success bg-success/10';
      if (numericValue < 0) return 'text-failure bg-failure/10';
      return 'text-neutral bg-neutral/5';
    }
  };

  /* Weight & Deduction */
  const renderValue = (
    value: FormulaValueWithMonetary,
    valueClassName?: string,
  ) => {
    if (type === 'weight') {
      return (
        <div className="flex flex-col items-end justify-center gap-0.5">
          {!!value.weight && (
            <div
              className={cn(
                'flex items-center gap-0.5 text-base font-bold text-neutral/80',
                valueClassName,
              )}
            >
              <MeasuredValue
                value={convertWeightToDisplayValue(
                  value.weight,
                  value.weight?.unit,
                )}
                unit={value.weight?.unit}
              />
            </div>
          )}
          {!!value.each && (
            <div className="flex items-center gap-0.5">
              <span className="text-base font-bold text-neutral">
                {humanReadableNumber(value.each, { locale: getLocale() })}
              </span>
              <span className="ml-[.25em] text-base font-light text-neutral/60">
                {humanReadableShortPricingUnit('Each')}
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-end justify-center gap-0.5">
        <span className={cn('text-base text-neutral/60', valueClassName)}>
          {valuePrefix}
        </span>
        <span
          className={cn('text-base font-bold text-neutral', valueClassName)}
        >
          {formatValue?.(value.monetaryValue ?? 0)}
        </span>
        <span className="text-base text-neutral/60">{valueSuffix}</span>
      </div>
    );
  };

  /* Delta */
  const renderDeltaValue = (value: FormulaValueWithMonetary) => {
    if (type === 'weight') {
      return (
        <div className="flex flex-col items-end justify-center gap-0.5">
          {!!value.weight && (
            <div
              className={cn(
                'flex items-center gap-0.5 text-base font-bold text-neutral/80',
                value.weight.value < 0 ? deductionClassName : '',
              )}
            >
              <MeasuredValue
                value={convertWeightToDisplayValue(
                  value.weight,
                  value.weight.unit,
                )}
                unit={value.weight.unit}
              />
            </div>
          )}
          {!!value.each && (
            <div className="flex items-center gap-0.5">
              <span
                className={cn(
                  'text-base font-bold',
                  value.each < 0 ? deductionClassName : '',
                )}
              >
                {humanReadableNumber(value.each, { locale: getLocale() })}
              </span>
              <span className="ml-[.25em] text-base font-light text-neutral/60">
                {humanReadableShortPricingUnit('Each')}
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-end justify-center gap-0.5">
        <span className="text-base">{valuePrefix}</span>
        <span className="text-base font-bold">
          {formatValue?.(value.monetaryValue ?? 0)}
        </span>
        <span className="text-xs">{valueSuffix}</span>
      </div>
    );
  };

  /* Full Breakdown */
  return (
    <div className="@FormulaDisplay flex items-start gap-2 text-sm">
      {/* Net Shipped */}
      <div className="flex flex-col items-end gap-0.5">
        <div className="text-xs font-medium tracking-wide text-neutral/60 uppercase">
          {titles.netShipped}
        </div>
        {renderValue(netShipped)}
      </div>

      <div className="text-base font-light text-neutral/60">−</div>

      {/* Net Received */}
      <div className="flex flex-col items-end justify-end gap-0.5">
        <div className="text-xs font-medium tracking-wide text-neutral/60 uppercase">
          {titles.netReceived}
        </div>
        {renderValue(netReceived)}
      </div>

      <div className="text-base font-light text-neutral/60">−</div>

      {/* Deduction */}
      <div className="flex flex-col items-end gap-0.5">
        <div className="text-xs font-medium tracking-wide text-neutral/60 uppercase">
          {titles.deduction}
        </div>
        <div>{renderValue(deduction, deductionClassName)}</div>
      </div>

      <div className="text-base font-light text-neutral/60">=</div>

      {/* Delta */}
      <div
        className={cn(
          'my-[-1] flex flex-col items-end gap-0.5 rounded-md px-2 py-1',
          getDeltaColor(delta),
        )}
      >
        <div className="text-xs font-medium tracking-wide text-neutral/60 uppercase">
          {titles.delta}
        </div>
        {renderDeltaValue(delta)}
      </div>
    </div>
  );
};

type ReconciliationFooterProps = {
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
};

const loadMaterialsFormSelector = (
  state: MaterialsReconciliationFormState,
) => ({
  loadId: state.loadId,
  loadReconciliationId: state.loadReconciliationId,
  receivedLineItems: state.receivedLineItems,
  shippedLineItems: state.shippedLineItems,
  deductions: state.deductions,
  freightCharges: state.freightCharges,
});

export const ReconciliationSummary: React.FC<ReconciliationFooterProps> = ({
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
}) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () =>
      selectAtom(
        $materialsReconciliationForm,
        loadMaterialsFormSelector,
        isEqual,
      ),
    [],
  );
  const formState = useAtomValue(selectedFormAtom);

  const shippedLineItems = formState?.shippedLineItems;
  const receivedLineItems = formState?.receivedLineItems;

  const summary = useReconciliationSummary({
    shippedLineItems: shippedLineItems ?? [],
    receivedLineItems: receivedLineItems,
    deductions: formState.deductions,
    freightCharges: formState.freightCharges,
    weightUnit,
    facilityDefaultUnit,
  });

  return (
    <div className="@ReconciliationSummary flex flex-row items-start justify-end gap-6">
      {/* Weight Formula - show if there are weight or each items */}
      <FormulaDisplay
        type="weight"
        netShipped={summary.netShipped}
        netReceived={summary.netReceived}
        deduction={summary.deduction}
        delta={summary.delta}
        titles={{
          netReceived: t.materialsReconciliation.footer.netReceived,
          netShipped: t.materialsReconciliation.footer.netShipped,
          deduction: t.materialsReconciliation.footer.deduction,
          delta: t.materialsReconciliation.footer.delta,
        }}
      />

      {/* Monetary Formula */}
      <FormulaDisplay
        type="monetary"
        netShipped={{ monetaryValue: summary.shippedLoadTotal }}
        netReceived={{ monetaryValue: summary.receivedLoadTotal }}
        deduction={{ monetaryValue: summary.totalDeductionsAmount }}
        delta={{ monetaryValue: summary.moneyDelta }}
        formatValue={(value) =>
          humanReadableCurrency(value, {
            locale: getLocale(),
            currencyCode: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            includeSymbol: false,
          })
        }
        valuePrefix="$"
        titles={{
          netReceived: t.materialsReconciliation.footer.receivedTotal,
          netShipped: t.materialsReconciliation.footer.shippedTotal,
          deduction: t.materialsReconciliation.footer.deduction,
          delta: t.materialsReconciliation.footer.delta,
        }}
      />
    </div>
  );
};
